import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InboundAuthorizationDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly, money, relative } from '@/lib/utils';
import { tr } from '@/lib/i18n-core';
import { DataTable, Dialog, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { describeIntegrationError } from '../integrations/external-outcome';

interface CoverageOption {
  id: string;
  payerId: string;
  payer: { id: string; name: string };
  policyNo: string;
  isActive: boolean;
}

function DecideDialog({ item, onClose }: { item: InboundAuthorizationDto; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [patientId, setPatientId] = useState(item.matchedPatientId ?? '');
  const [coverageId, setCoverageId] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [dismissReason, setDismissReason] = useState('');
  const [mismatch, setMismatch] = useState<string | null>(null);

  const coverages = useQuery({
    queryKey: ['patient-coverages', patientId],
    queryFn: () => get<CoverageOption[]>(`/insurance/patients/${patientId}/coverages`),
    enabled: Boolean(patientId),
  });

  const done = (title: string) => {
    toast.success(title);
    void queryClient.invalidateQueries({ queryKey: ['nphies-inbound'] });
    void queryClient.invalidateQueries({ queryKey: ['nphies-poll'] });
    onClose();
  };

  const link = useMutation({
    mutationFn: () =>
      post<InboundAuthorizationDto>(`/integrations/nphies/inbound-authorizations/${item.id}/link`, {
        patientId,
        ...(coverageId ? { coverageId } : {}),
        ...(mismatch && overrideReason.trim().length >= 5 ? { overrideReason: overrideReason.trim() } : {}),
      }),
    onSuccess: () => done('Filed as an approved pre-authorisation'),
    onError: (error) => {
      const text = describeIntegrationError(error);
      if (/does not match/i.test(text)) setMismatch(text);
      else toast.error('Could not file the approval', text);
    },
  });

  const dismiss = useMutation({
    mutationFn: () => post<InboundAuthorizationDto>(`/integrations/nphies/inbound-authorizations/${item.id}/dismiss`, { reason: dismissReason.trim() }),
    onSuccess: () => done('Set aside'),
    onError: (error) => toast.error('Could not set it aside', describeIntegrationError(error)),
  });

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`Approval ${item.preauthRef ?? item.responseIdentifierValue ?? ''} from ${item.payerName ?? item.payerLicense ?? 'an insurer'}`}
      description={`For ${item.patientName ?? 'a patient'} (ID ${item.patientIdValue ?? 'not given'}) · received ${relative(item.receivedAt)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => link.mutate()} loading={link.isPending} disabled={!patientId || Boolean(mismatch && overrideReason.trim().length < 5)}>
            File under this patient
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <DataTable
          dense
          rows={item.lines}
          rowKey={(row) => String(row.sequence)}
          empty="The insurer sent no lines."
          columns={[
            { header: 'Service', cell: (row) => `${row.code ?? ''} ${row.display ?? ''}` },
            { header: 'Quantity', align: 'right', cell: (row) => row.quantity ?? '—' },
            { header: 'Approved', align: 'right', cell: (row) => (row.approvedMinor === null ? '—' : money(row.approvedMinor)) },
          ]}
        />
        {item.periodFrom && (
          <p className="text-xs text-muted-foreground">
            Valid {dateOnly(item.periodFrom)} to {item.periodTo ? dateOnly(item.periodTo) : 'open'}
          </p>
        )}
        <Field label="Patient" required hint={item.matchedPatientId ? 'Found by the national ID the insurer sent' : 'No patient here has the national ID the insurer sent; pick them only if you are sure'}>
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        {patientId && (
          <Field label="Policy" htmlFor="inbound-coverage" hint="Leave on the automatic choice to use the patient’s policy with this insurer">
            <Select id="inbound-coverage" value={coverageId} onChange={(e) => setCoverageId(e.target.value)}>
              <option value="">Automatic</option>
              {(coverages.data ?? []).filter((coverage) => coverage.isActive).map((coverage) => (
                <option key={coverage.id} value={coverage.id}>
                  {coverage.payer.name} · {coverage.policyNo}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {mismatch && (
          <Alert variant="destructive" title="This does not match what is on file">
            {mismatch}
            {can('nphies.inbound.override') ? (
              <Field label="Why file it anyway" htmlFor="inbound-override" required>
                <Input id="inbound-override" maxLength={300} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              </Field>
            ) : (
              <p className="mt-1 text-xs">Ask someone who may override insurer approvals, or set it aside.</p>
            )}
          </Alert>
        )}
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <Field label="Set aside instead" htmlFor="inbound-dismiss" className="flex-1">
            <Input id="inbound-dismiss" maxLength={300} placeholder="Why, for example a duplicate" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} />
          </Field>
          <Button variant="outline" loading={dismiss.isPending} disabled={dismissReason.trim().length < 3} onClick={() => dismiss.mutate()}>
            Set aside
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export function InboundApprovalsPanel() {
  const { can } = useAuth();
  const [status, setStatus] = useState<'open' | 'linked' | 'dismissed'>('open');
  const [open, setOpen] = useState<InboundAuthorizationDto | null>(null);
  const list = useQuery({
    queryKey: ['nphies-inbound', status],
    queryFn: () => get<{ items: InboundAuthorizationDto[]; nextCursor: string | null }>(`/integrations/nphies/inbound-authorizations${qs({ status, limit: 50 })}`),
  });
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold">Approvals sent by insurers</h3>
      <p className="mb-2 text-xs text-muted-foreground">An insurer can approve services without a request from here. File each one under the patient it names; nothing creates a patient on its own.</p>
      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'status')}>
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="open">To file</option>
              <option value="linked">Filed</option>
              <option value="dismissed">Set aside</option>
            </Select>
          </FilterField>
        }
        onReset={status !== 'open' ? () => setStatus('open') : undefined}
      />
      <DataTable
        rows={list.data?.items}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.id}
        onRowClick={status === 'open' && can('nphies.inbound.link') ? (row) => setOpen(row) : undefined}
        empty="Nothing here."
        columns={[
          { header: 'Received', sort: (row) => row.receivedAt, cell: (row) => <span className="text-xs">{relative(row.receivedAt)}</span> },
          { header: 'Insurer', sort: (row) => row.payerName ?? row.payerLicense, cell: (row) => row.payerName ?? row.payerLicense ?? 'Not recognised' },
          { header: 'Patient', sort: (row) => row.patientName, cell: (row) => `${row.patientName ?? ''} (${row.patientIdValue ?? 'no ID'})${row.matchedPatientId ? '' : ' · not found here'}` },
          { header: 'Approval', sort: (row) => row.preauthRef ?? row.responseIdentifierValue, cell: (row) => row.preauthRef ?? row.responseIdentifierValue ?? '—' },
          { header: 'Lines', align: 'right', sort: (row) => row.lines.length, cell: (row) => row.lines.length },
        ]}
      />
      {open && <DecideDialog item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
